/**
 * Tempo estimado de leitura da notícia ("Tempo estimado de leitura: 7 minutos").
 *
 * 200 palavras/minuto é deliberadamente conservador. A meta-análise de
 * Brysbaert (2019) aponta ~238 wpm para leitura silenciosa de não-ficção em
 * inglês, e o português tem palavras mais longas — arredondar para baixo faz a
 * estimativa nunca subestimar, que é o erro que o leitor percebe.
 *
 * Imagens entram no cálculo no modelo do Medium: 12s pela primeira, caindo 1s a
 * cada imagem seguinte até o piso de 3s — quem rola uma galeria olha cada foto
 * por menos tempo que a primeira.
 */

const WORDS_PER_MINUTE = 200;
const FIRST_IMAGE_SECONDS = 12;
const MIN_IMAGE_SECONDS = 3;
const SECONDS_PER_MINUTE = 60;

/** Blocos que contam como imagem para o acréscimo de segundos. */
const IMAGE_BLOCK_TYPES = new Set(['image', 'imagesGrid', 'video']);

interface SlateNode {
  text?: string;
  children?: SlateNode[];
}

interface Block {
  '@type'?: string;
  plaintext?: string;
  value?: SlateNode[];
  [key: string]: unknown;
}

/** Texto de um nó Slate, descendo pela árvore de `children`. */
const slateNodeText = (node: SlateNode): string => {
  if (typeof node?.text === 'string') return node.text;
  if (!Array.isArray(node?.children)) return '';
  return node.children.map(slateNodeText).join(' ');
};

/**
 * Texto de um bloco. `plaintext` cobre os blocos vindos do `html2blocks` da
 * migração; `value` cobre os escritos no volto-slate, que nem sempre têm o
 * `plaintext` preenchido.
 */
export const blockText = (block?: Block): string => {
  if (!block) return '';
  if (typeof block.plaintext === 'string' && block.plaintext.trim()) {
    return block.plaintext;
  }
  if (Array.isArray(block.value)) {
    return block.value.map(slateNodeText).join(' ');
  }
  return '';
};

/** Remove tags de um fragmento HTML (descrição, resumo, assinatura). */
export const stripHtml = (html?: string): string =>
  (html || '').replace(/<[^>]*>/g, ' ');

export const countWords = (text: string): number =>
  text.trim() ? text.trim().split(/\s+/).length : 0;

/** Segundos somados por `count` imagens, decrescendo de 12s até 3s. */
export const imageSeconds = (count: number): number => {
  let total = 0;
  for (let i = 0; i < count; i += 1) {
    total += Math.max(MIN_IMAGE_SECONDS, FIRST_IMAGE_SECONDS - i);
  }
  return total;
};

export interface ReadingTimeInput {
  // `Record<string, any>`: o corpo da notícia é `Record<string, VoltoBlock>`, e
  // aqui só interessa `@type`/`plaintext`/`value` — `Block` acima descreve esse
  // subconjunto sem exigir o tipo completo do bloco do Volto.
  blocks?: Record<string, any>;
  blocks_layout?: { items?: string[] };
  description?: string;
  /** HTML do campo `resumo` (RichText). */
  resumo?: string;
}

/**
 * Minutos de leitura, sempre >= 1 — "0 minutos" não diz nada ao leitor, e toda
 * notícia publicada tem ao menos título e linha de apoio.
 */
export const getReadingTimeMinutes = ({
  blocks,
  blocks_layout: blocksLayout,
  description,
  resumo,
}: ReadingTimeInput = {}): number => {
  const order = blocksLayout?.items ?? Object.keys(blocks ?? {});

  let words = countWords(description || '') + countWords(stripHtml(resumo));
  let images = 0;

  order.forEach((id) => {
    const block = blocks?.[id];
    if (!block) return;
    if (IMAGE_BLOCK_TYPES.has(block['@type'] as string)) {
      images += 1;
    }
    words += countWords(blockText(block));
  });

  const seconds =
    (words / WORDS_PER_MINUTE) * SECONDS_PER_MINUTE + imageSeconds(images);

  return Math.max(1, Math.ceil(seconds / SECONDS_PER_MINUTE));
};

/** Rótulo pronto para a linha de metadados: `1 minuto` / `7 minutos`. */
export const formatReadingTime = (minutes: number): string =>
  `${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`;

export default getReadingTimeMinutes;
